<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useCommercialGoalsStore } from 'dashboard/stores/commercialGoals';

import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import CommercialGoalDialog from 'dashboard/components-next/CommercialGoals/CommercialGoalDialog.vue';
import CommercialGoalRow from 'dashboard/components-next/CommercialGoals/CommercialGoalRow.vue';

const { t } = useI18n();
const store = useCommercialGoalsStore();

// Cada aba vira um par de filtros da API (status + situation).
const FILTERS = {
  active: { status: 'active', situation: 'current' },
  scheduled: { status: 'active', situation: 'scheduled' },
  ended: { status: 'active', situation: 'ended' },
  inactive: { status: 'inactive' },
};
const filterKeys = Object.keys(FILTERS);

const filter = ref('active');
const page = ref(1);
const goalDialogRef = ref(null);
const deleteDialogRef = ref(null);
const goalToDelete = ref(null);

const tabs = computed(() =>
  filterKeys.map(key => ({
    label: t(`COMMERCIAL_GOALS.FILTERS.${key.toUpperCase()}`),
    key,
  }))
);
const activeTabIndex = computed(() => filterKeys.indexOf(filter.value));

const goals = computed(() => store.records);
const isFetching = computed(() => store.uiFlags.fetchingList);

const loadGoals = async () => {
  try {
    await store.get({ ...FILTERS[filter.value], page: page.value });
  } catch {
    useAlert(t('COMMERCIAL_GOALS.MESSAGES.LOAD_ERROR'));
  }
};

const changeFilter = tab => {
  filter.value = tab.key;
  page.value = 1;
};

watch([filter, page], loadGoals, { immediate: true });

const openCreate = () => goalDialogRef.value.open();

const openEdit = goal => goalDialogRef.value.open(goal);

const toggleStatus = async goal => {
  const isActive = goal.status === 'active';
  try {
    await store.update({
      id: goal.id,
      status: isActive ? 'inactive' : 'active',
    });
    useAlert(
      t(
        isActive
          ? 'COMMERCIAL_GOALS.MESSAGES.DEACTIVATED'
          : 'COMMERCIAL_GOALS.MESSAGES.REACTIVATED'
      )
    );
    loadGoals();
  } catch (error) {
    useAlert(error.message || t('COMMERCIAL_GOALS.MESSAGES.UPDATE_ERROR'));
  }
};

const askDelete = goal => {
  goalToDelete.value = goal;
  deleteDialogRef.value.open();
};

const confirmDelete = async () => {
  try {
    await store.delete(goalToDelete.value.id);
    deleteDialogRef.value.close();
    useAlert(t('COMMERCIAL_GOALS.MESSAGES.DELETED'));
    loadGoals();
  } catch {
    useAlert(t('COMMERCIAL_GOALS.MESSAGES.DELETE_ERROR'));
  }
};
</script>

<template>
  <SettingsLayout
    :is-loading="isFetching"
    :loading-message="t('COMMERCIAL_GOALS.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('COMMERCIAL_GOALS.TITLE')"
        :description="t('COMMERCIAL_GOALS.DESCRIPTION')"
      >
        <template #tabs>
          <TabBar
            :tabs="tabs"
            :initial-active-tab="activeTabIndex"
            @tab-changed="changeFilter"
          />
        </template>
        <template #actions>
          <Button
            :label="t('COMMERCIAL_GOALS.CREATE_ACTION')"
            size="sm"
            @click="openCreate"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <div
        v-if="!goals.length"
        class="flex flex-col items-center gap-4 py-20 text-center"
      >
        <span class="text-sm text-n-slate-11">
          {{ t('COMMERCIAL_GOALS.EMPTY') }}
        </span>
        <Button
          :label="t('COMMERCIAL_GOALS.CREATE_ACTION')"
          @click="openCreate"
        />
      </div>
      <div v-else class="flex flex-col gap-3">
        <CommercialGoalRow
          v-for="goal in goals"
          :key="goal.id"
          :goal="goal"
          @edit="openEdit"
          @toggle-status="toggleStatus"
          @delete="askDelete"
        />
        <PaginationFooter
          v-if="store.meta.count > store.meta.perPage"
          v-model:current-page="page"
          :total-items="store.meta.count"
          :items-per-page="store.meta.perPage"
        />
      </div>
    </template>

    <CommercialGoalDialog ref="goalDialogRef" @saved="loadGoals" />

    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="t('COMMERCIAL_GOALS.DELETE.TITLE')"
      :description="
        t('COMMERCIAL_GOALS.DELETE.MESSAGE', { title: goalToDelete?.title })
      "
      :confirm-button-label="t('COMMERCIAL_GOALS.DELETE.CONFIRM')"
      :is-loading="store.uiFlags.deletingItem"
      @confirm="confirmDelete"
    />
  </SettingsLayout>
</template>
